import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  FileText,
  FileUp,
  LoaderCircle,
  LockKeyhole,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { supabase } from "../supabaseClient";

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ANALYSIS_STEPS = [
  "Reading your CV",
  "Understanding the job description",
  "Identifying skills and keywords",
  "Calculating your ATS match",
  "Preparing recommendations",
];

const WORKFLOW_STEPS = ["Upload CV", "Job Description", "Analyse", "Improve"];

const UploadAndJobDescription = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [file, setFile] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [analysisStep, setAnalysisStep] = useState(0);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const fetchUserEmail = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user?.email) setEmail(user.email);
    };

    fetchUserEmail();
  }, []);

  useEffect(() => {
    if (!loading || reduceMotion) return undefined;

    const interval = window.setInterval(() => {
      setAnalysisStep((current) => Math.min(current + 1, ANALYSIS_STEPS.length - 1));
    }, 1600);

    return () => window.clearInterval(interval);
  }, [loading, reduceMotion]);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const getCurrentStep = () => {
    if (loading) return 2;
    if (file && jobDescription.trim()) return 2;
    if (file) return 1;
    return 0;
  };

  const selectFile = (selectedFile) => {
    setErrors((current) => ({ ...current, file: "", form: "" }));

    if (!selectedFile) return;
    if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
      setFile(null);
      setErrors((current) => ({
        ...current,
        file: "Please choose a PDF or DOCX file.",
      }));
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFile(null);
      setErrors((current) => ({
        ...current,
        file: "This file is larger than 2 MB. Please upload a smaller CV.",
      }));
      return;
    }

    setFile(selectedFile);
  };

  const handleFileChange = (event) => selectFile(event.target.files?.[0]);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    selectFile(event.dataTransfer.files?.[0]);
  };

  const removeFile = () => {
    setFile(null);
    setErrors((current) => ({ ...current, file: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const friendlyApiError = (status) => {
    if (status === 401) return "Your session has expired. Please sign in again and retry.";
    if (status === 403) return "Your current usage limit has been reached. Visit Pricing to review your options.";
    if (status === 413) return "The uploaded file is too large. Please use a CV smaller than 2 MB.";
    if (status >= 500) return "We could not analyse your resume right now. Please try again shortly.";
    return "We could not analyse this resume. Check your details and try again.";
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = "Enter your name to continue.";
    if (!email) nextErrors.email = "We could not find your account email. Please sign in again.";
    else if (!validateEmail(email)) nextErrors.email = "Your account email is not valid.";
    if (!file) nextErrors.file = "Upload your current CV to continue.";
    if (!jobDescription.trim()) {
      nextErrors.jobDescription = "Paste the job description you want to compare against.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || !validateForm()) return;

    setLoading(true);
    setAnalysisStep(0);
    setErrors({});

    try {
      localStorage.setItem("jobDescription", jobDescription.trim());

      const formData = new FormData();
      formData.append("name", name);
      formData.append("email", email);
      formData.append("file", file);
      formData.append("job_description", jobDescription);

      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "https://jobzam-backend.onrender.com";
      const response = await fetch(`${backendUrl}/match/`, {
        method: "POST",
        body: formData,
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) throw new Error(friendlyApiError(response.status));

      navigate("/results", {
        state: {
          analysis: data.results,
          matchPercentage: parseFloat(data.results.match_percentage || 0).toFixed(1),
          atsScore: parseFloat(data.results.ats_score || 0).toFixed(1),
          matchedKeywords: data.results.matched_keywords || [],
          missingKeywords: data.results.missing_keywords || [],
          name,
          email,
          originalResume: data.results.original_resume || "",
          jobDescription: jobDescription.trim(),
        },
      });
    } catch (error) {
      console.error("Resume analysis request failed:", error);
      setErrors({
        form: error.message || "We could not analyse your resume. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const currentStep = getCurrentStep();

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-50 px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-blue-50 to-transparent" />
      <div className="pointer-events-none absolute -left-32 top-24 h-80 w-80 rounded-full bg-cyan-100/40 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 top-10 h-96 w-96 rounded-full bg-blue-100/50 blur-3xl" />

      <motion.main
        className="relative mx-auto w-full max-w-6xl"
        initial={reduceMotion ? false : { opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        <header className="mb-8 max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-blue-700 shadow-sm">
            <Sparkles aria-hidden="true" className="h-3.5 w-3.5" />
            Resume Optimizer
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
            Optimise your resume for the job <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">you want.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
            Upload your CV and add the job description to see your ATS match, missing keywords, and role-specific improvements.
          </p>
        </header>

        <nav aria-label="Resume optimisation progress" className="scrollbar-hide mb-7 max-w-full touch-pan-x overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-200/80 bg-white p-2 shadow-sm">
          <ol className="flex min-w-[620px] items-center">
            {WORKFLOW_STEPS.map((step, index) => {
              const complete = index < currentStep;
              const active = index === currentStep;
              return (
                <li key={step} className="flex flex-1 items-center last:flex-none">
                  <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${active ? "bg-blue-50 text-blue-700" : complete ? "text-slate-800" : "text-slate-400"}`} aria-current={active ? "step" : undefined}>
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${active ? "bg-blue-600 text-white" : complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {complete ? <Check aria-hidden="true" className="h-4 w-4" /> : String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="whitespace-nowrap">{step}</span>
                  </div>
                  {index < WORKFLOW_STEPS.length - 1 && <div aria-hidden="true" className={`mx-2 h-px flex-1 ${complete ? "bg-blue-300" : "bg-slate-200"}`} />}
                </li>
              );
            })}
          </ol>
        </nav>

        {loading ? (
          <section className="rounded-3xl border border-blue-100 bg-white p-6 shadow-[0_24px_70px_-36px_rgba(37,99,235,0.35)] sm:p-10" aria-live="polite" aria-busy="true">
            <div className="mx-auto max-w-2xl text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-200">
                <LoaderCircle aria-hidden="true" className={`h-7 w-7 ${reduceMotion ? "" : "animate-spin"}`} />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-slate-950">Analysing your resume</h2>
              <p className="mt-2 text-sm text-slate-600">Keep this page open while we prepare your personalised ATS insights.</p>
              <ol className="mt-8 space-y-3 text-left">
                {ANALYSIS_STEPS.map((step, index) => (
                  <li key={step} className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-300 ${index === analysisStep ? "border-blue-200 bg-blue-50 text-blue-800" : index < analysisStep ? "border-emerald-100 bg-emerald-50/60 text-slate-700" : "border-slate-100 text-slate-400"}`}>
                    {index < analysisStep ? <CheckCircle2 aria-hidden="true" className="h-5 w-5 text-emerald-600" /> : index === analysisStep ? <LoaderCircle aria-hidden="true" className={`h-5 w-5 text-blue-600 ${reduceMotion ? "" : "animate-spin"}`} /> : <span className="h-5 w-5 rounded-full border-2 border-slate-200" />}
                    <span className="font-medium">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {errors.form && (
              <div role="alert" className="mb-5 flex items-start justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <p>{errors.form}</p>
                <button type="button" onClick={() => setErrors((current) => ({ ...current, form: "" }))} className="shrink-0 font-semibold underline underline-offset-2">Dismiss</button>
              </div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
              <section className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-[0_20px_60px_-38px_rgba(15,23,42,0.35)] sm:p-7">
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">Step 01</p>
                  <h2 className="mt-1 text-xl font-bold text-slate-950">Upload your current CV</h2>
                  <p className="mt-1 text-sm text-slate-500">PDF or DOCX · Maximum file size 2 MB</p>
                </div>

                <div
                  className={`relative flex min-h-[220px] flex-col items-center justify-center rounded-2xl border-2 border-dashed px-5 py-7 text-center transition-colors duration-200 ${dragActive ? "border-blue-500 bg-blue-50" : errors.file ? "border-red-300 bg-red-50/40" : file ? "border-emerald-300 bg-emerald-50/40" : "border-blue-200 bg-blue-50/40 hover:border-blue-400 hover:bg-blue-50"}`}
                  onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                >
                  <input ref={fileInputRef} id="file-upload" name="file" type="file" accept=".pdf,.docx" onChange={handleFileChange} className="sr-only" aria-describedby={errors.file ? "file-error" : "file-help"} />
                  {file ? (
                    <>
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700"><FileText aria-hidden="true" className="h-6 w-6" /></div>
                      <p className="mt-4 w-full max-w-full truncate px-2 font-semibold text-slate-900" title={file.name}>{file.name}</p>
                      <p className="mt-1 text-sm text-slate-500">{(file.size / (1024 * 1024)).toFixed(2)} MB · Ready to analyse</p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        <label htmlFor="file-upload" className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-700"><RotateCcw aria-hidden="true" className="h-4 w-4" /> Replace</label>
                        <button type="button" onClick={removeFile} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"><Trash2 aria-hidden="true" className="h-4 w-4" /> Remove</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-blue-700"><FileUp aria-hidden="true" className="h-7 w-7" /></div>
                      <p className="mt-4 font-semibold text-slate-900">Drag and drop your CV here</p>
                      <p id="file-help" className="mt-1 text-sm text-slate-500">or select a file from your device</p>
                      <label htmlFor="file-upload" className="mt-4 cursor-pointer rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-blue-700 shadow-sm ring-1 ring-inset ring-blue-200 transition hover:bg-blue-50 focus-within:ring-2 focus-within:ring-blue-600">Browse files</label>
                    </>
                  )}
                </div>
                {errors.file && <p id="file-error" role="alert" className="mt-2 text-sm font-medium text-red-600">{errors.file}</p>}

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-semibold text-slate-700">Your name</label>
                    <input id="name" name="name" type="text" value={name} onChange={(event) => { setName(event.target.value); setErrors((current) => ({ ...current, name: "" })); }} placeholder="Enter your full name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "name-error" : undefined} className={`w-full rounded-xl border bg-white px-3.5 py-3 text-slate-900 outline-none transition focus:ring-4 focus:ring-blue-100 ${errors.name ? "border-red-300" : "border-slate-200 focus:border-blue-500"}`} />
                    {errors.name && <p id="name-error" role="alert" className="mt-1.5 text-sm text-red-600">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-slate-700">Account email</label>
                    <input id="email" name="email" type="email" value={email} readOnly autoComplete="off" placeholder="Fetching your email…" aria-invalid={Boolean(errors.email)} className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-slate-600 outline-none" />
                    {errors.email && <p role="alert" className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
                  </div>
                </div>
              </section>

              <section className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-5 shadow-[0_20px_60px_-38px_rgba(15,23,42,0.35)] sm:p-7">
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">Step 02</p>
                  <h2 className="mt-1 text-xl font-bold text-slate-950">Add the job description</h2>
                  <p className="mt-1 text-sm text-slate-500">Include the full role details for a more useful comparison.</p>
                </div>
                <label htmlFor="job-description" className="mb-2 block text-sm font-semibold text-slate-700">Job description</label>
                <textarea id="job-description" name="jobDescription" value={jobDescription} onChange={(event) => { const value = event.target.value; setJobDescription(value); localStorage.setItem("jobDescription", value); setErrors((current) => ({ ...current, jobDescription: "" })); }} placeholder="Paste the full job description here, including responsibilities, requirements, and preferred skills." aria-invalid={Boolean(errors.jobDescription)} aria-describedby={errors.jobDescription ? "job-description-error" : "job-description-count"} className={`min-h-[305px] w-full flex-1 resize-y rounded-2xl border bg-slate-50/60 p-4 text-[15px] leading-6 text-slate-900 outline-none transition placeholder:text-slate-400 focus:bg-white focus:ring-4 focus:ring-blue-100 ${errors.jobDescription ? "border-red-300" : "border-slate-200 focus:border-blue-500"}`} />
                <div className="mt-2 flex items-start justify-between gap-4">
                  <div>{errors.jobDescription && <p id="job-description-error" role="alert" className="text-sm font-medium text-red-600">{errors.jobDescription}</p>}</div>
                  <p id="job-description-count" className="shrink-0 text-xs tabular-nums text-slate-400">{jobDescription.length.toLocaleString()} characters</p>
                </div>
              </section>
            </div>

            <div className="mt-6 rounded-3xl border border-slate-200/80 bg-white p-4 shadow-sm sm:flex sm:items-center sm:justify-between sm:gap-6 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><ShieldCheck aria-hidden="true" className="h-5 w-5" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Your CV is used only to generate your results.</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500"><LockKeyhole aria-hidden="true" className="h-3.5 w-3.5" /> Secure sign-in required · No changes are sent anywhere automatically.</p>
                </div>
              </div>
              <button type="submit" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-200/70 transition duration-200 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-0 sm:w-auto" disabled={loading}>
                <Sparkles aria-hidden="true" className="h-4 w-4" /> Analyse My Resume <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>
          </form>
        )}
      </motion.main>
    </div>
  );
};

export default UploadAndJobDescription;
